// =============================================================================
// GlobalStyle.ts - CSS reset, font-face declarations, and global styles
// =============================================================================
// Exported as a template string for injection via MUI CssBaseline.
// =============================================================================

import { colors } from "./colors"
import { fontFamily, fontWeights } from "./typography"

// ---------------------------------------------------------------------------
// Global style string
// Injected via MuiCssBaseline styleOverrides in theme.ts
// ---------------------------------------------------------------------------
export const GlobalStyle = `
  /* ---- CSS Reset ---- */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* ---- Root direction and font ---- */
  :root {
    direction: rtl;
    font-family: ${fontFamily.primary}, sans-serif;
  }

  html {
    overscroll-behavior-x: none;
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
    /* EGDE-719: anchor landings are travelled, not jumped. The page's fragment
       is scrolled to by the BROWSER — opening /#FaqAnchor, and every plain
       in-page anchor link — and that scroll takes its behaviour from here, so
       this is the only place that can animate it. AnchorScrollSettle's own
       re-aiming passes suppress it through scrollInstantly(): they are
       corrections to a landing, not journeys, and animating each of forty frames
       would have them chase each other. Programmatic scrolls that must land at
       once (Header's pinScrollTop) opt out the same way. The html element also
       carries data-scroll-behavior="smooth" so Next keeps route-change scrolls
       instant. Scroll-behavior does not inherit, so scrollers inside the page
       are untouched. */
    scroll-behavior: smooth;
  }

  @media (prefers-reduced-motion: reduce) {
    html {
      scroll-behavior: auto;
    }
  }

  body {
    font-family: ${fontFamily.primary}, sans-serif;
    /* EGDE-738: the document's default text weight. Without this the UA default
       of 400 leaks into anything the theme's typography variants never reach —
       measured, the FAQ search placeholder and the CV-upload label. Body copy on
       this site is 500, so the document should say so rather than leaving each
       component to remember. */
    font-weight: ${fontWeights.medium};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* ---- Form controls inherit the brand font (EGDE-621) ----
     Browsers do NOT inherit font-family into form controls — they impose their
     own UA default (Arial on Windows Chrome). Every styled button that didn't
     declare a family of its own was therefore rendering in Arial instead of the
     brand font: QA caught it on the language switcher, but it affected 44 of the
     52 button styles across the packages. Element-level selector on purpose —
     any component that sets its own font-family wins on specificity, so this
     only fills in the ones that never set one. */
  button,
  input,
  select,
  textarea,
  optgroup {
    font-family: inherit;
    /* EGDE-738: font-weight does NOT inherit into form controls on its own —
       the UA font shorthand pins them to 400 — so the brand weight has to be
       passed in explicitly, exactly as the family above is. */
    font-weight: inherit;
  }

  /* ---- Focus-visible accessibility ---- */
  *:focus-visible {
    outline: 3px dashed #000000;
    outline-offset: 2px;
  }

  /* Remove default focus styles for mouse users */
  *:focus:not(:focus-visible) {
    outline: none;
  }

  /* ---- Webkit autofill background fix ---- */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0px 1000px white inset !important;
    box-shadow: 0 0 0px 1000px white inset !important;
  }

  /* ---- Number input spinner removal ---- */
  input::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
  }

  /* ---- Text selection (EGDE-453 / EGDE-454 / EGDE-661) ----
     Brand-orange highlight with BLACK text per the Figma marking state,
     applied globally so every page (desktop and mobile) gets it. Note:
     iOS Safari draws its own native selection overlay and ignores custom
     ::selection styling.

     EGDE-661: the highlight was the full-strength brand orange (primary500,
     #f86f3e), which QA read as dark and harsh under body copy. The ticket points
     at a Figma frame with "a lighter background color in the design" — that
     marking state samples to #ffb36e on black, i.e. the secondary500 token (the
     light end of the brand gradient), so the value below is the design's, not a
     tint of our choosing. The ticket's other suggestion, keeping the strong
     orange and turning the text white, is not available to us: white on #f86f3e
     is 2.86:1, below the 4.5:1 AA floor for body text (the Egged site can do
     white-on-green only because that green is dark). Black on secondary500 is
     11.9:1. */
  ::selection {
    background-color: ${colors.primitives.secondary500};
    color: #000000;
  }

  /* ---- Hero brand splash (EGDE-460) ----
     Scroll must be locked the moment the splash is on screen — the React
     effect that sets body overflow only runs after hydration, leaving a
     scrollable gap on slow loads. The splash overlay carries
     [data-hero-intro] in the SSR markup, so this rule locks scroll from the
     very first paint and releases automatically when the overlay unmounts.
     A synchronous inline script (HeroBanner) stamps <html data-intro-played>
     on repeat visits in the same session: the overlay is then hidden before
     it can flash, and no scroll lock applies. */
  html:not([data-intro-played]):has([data-hero-intro]) {
    overflow: hidden;
  }
  html[data-intro-played] [data-hero-intro] {
    display: none !important;
  }
  /* EGDE-483: the prototype keeps the header chrome hidden for the splash's
     whole lifetime — it may only appear once the aleph window has expanded
     into the full-bleed hero. Previously the header bled through as the
     overlay faded. When the overlay unmounts this rule releases and the
     header's own 0.3s opacity transition fades it in. Gated on
     :not([data-intro-played]) so repeat visits (splash skipped, attribute
     stamped pre-paint) never blank the header for a frame. */
  html:not([data-intro-played]):has([data-hero-intro])
    [data-component="header"] {
    opacity: 0;
    pointer-events: none;
  }
  /* EGDE-483 r2: after a REAL splash run (HeroIntro stamps
     data-intro-finished at unmount — skip paths never do), the homepage must
     first stand header-less for a beat, then the header pops in from ABOVE.
     "backwards" keeps it parked off-screen through the delay. */
  @keyframes introHeaderIn {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  html[data-intro-finished] [data-component="header"] {
    animation: introHeaderIn 0.7s cubic-bezier(0.33, 1, 0.68, 1) 0.55s
      backwards;
  }

  /* ---- Strong tag ---- */
  strong {
    font-weight: 700;
  }

  /* ---- Links ---- */
  a {
    color: inherit;
    text-decoration: none;
  }

  /* ---- Images ---- */
  img {
    max-width: 100%;
    display: block;
  }

  /* ---- Scrollbar styling ---- */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
  /* Firefox */
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  }

  /* ---- Swiper overrides (preserved from legacy) ---- */
  .swiper {
    width: 100%;
    padding-top: 50px;
    padding-bottom: 50px;
  }
  .swiper-slide {
    background-position: center;
    background-size: cover;
    width: calc(100% - 40px);
  }
  .swiper-slide img {
    display: block;
    width: 100%;
  }
  .swiper-button-disabled {
    opacity: 0.3;
  }
`
